import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  doc,
  onSnapshot,
  runTransaction,
  collection,
  query,
  orderBy,
} from "firebase/firestore";
import { database } from "../../database/firebase";
import {
  COLS,
  ROWS,
  P1,
  P2,
  EMPTY,
  GAME_MODE,
  boardFromFlat,
  getDropRow,
  resolvePlayerForUser,
  applyMove,
  parseConnect4State,
  decodeWinningCells,
  isColumnFull,
  applyTurnTimeout,
  TURN_LIMIT_SEC,
} from "../../game/connectFour";
import {
  buildLeaderboardAfterRound,
  getConnect4LeaderboardRef,
} from "../../game/connect4Series";
import { pickCpuColumn } from "../../game/connectFourCpu";
import {
  isCpuModeSelected,
  loadCpuGame,
  saveCpuGame,
  syncCpuSeriesAfterRound,
  startFreshCpuGame,
} from "../../game/gameModeStorage";
import "./game.css";

const CURRENT_LOBBY_KEY = "mythFactCurrentLobby";

function readStoredUserData() {
  try {
    const raw =
      localStorage.getItem("userData") ||
      sessionStorage.getItem("userData") ||
      "{}";
    return JSON.parse(raw) || {};
  } catch {
    return {};
  }
}

function resolveLobbyName(userData) {
  try {
    const fromSession = sessionStorage.getItem(CURRENT_LOBBY_KEY);
    if (fromSession?.trim()) return fromSession.trim();
  } catch {
    // ignore
  }
  return String(userData?.lobby ?? "").trim();
}

function normalizeCharacterName(character) {
  const value = String(character || "").trim().toLowerCase();
  return /^c[1-9]$/.test(value) ? value : "c1";
}

function characterAvatarSrc(character) {
  const id = normalizeCharacterName(character);
  return `/img/${id}.png`;
}

function getPlayerLabel(player, fallback, order) {
  const name = String(player?.name ?? "").trim();
  if (name) return name;
  const email = String(player?.email ?? "").trim();
  if (email) return email;
  const fb = String(fallback?.name ?? "").trim();
  if (fb) return fb;
  return `Player ${order}`;
}

function formatTurnClock(totalSeconds) {
  const s = Math.max(0, Math.floor(totalSeconds));
  return `0:${String(s).padStart(2, "0")}`;
}

function isWinningCell(winningCells, row, col) {
  if (!Array.isArray(winningCells)) return false;
  return winningCells.some(
    (cell) => Array.isArray(cell) && cell[0] === row && cell[1] === col
  );
}

function GameInfoModeIcon({ cpu }) {
  if (cpu) {
    return (
      <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <rect x="6" y="8" width="12" height="10" rx="2" stroke="currentColor" strokeWidth="1.6" />
        <circle cx="9.5" cy="13" r="1" fill="currentColor" />
        <circle cx="14.5" cy="13" r="1" fill="currentColor" />
        <path d="M12 5v2" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
      </svg>
    );
  }
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="8" cy="9" r="3" stroke="currentColor" strokeWidth="1.6" />
      <path
        d="M4 18c0-2.2 1.8-4 4-4s4 1.8 4 4"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
      />
      <circle cx="16.5" cy="9.5" r="2.6" stroke="currentColor" strokeWidth="1.6" />
      <path
        d="M12.5 18c0-1.8 1.5-3.2 4-3.2s4 1.4 4 3.2"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
      />
    </svg>
  );
}

function GameInfoMovesIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="12" cy="12" r="8.5" stroke="currentColor" strokeWidth="1.6" />
      <path
        d="M9 12h6M12 9v6"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
      />
      <circle cx="12" cy="12" r="1.5" fill="currentColor" />
    </svg>
  );
}

const RED_CROWN_SRC = `${process.env.PUBLIC_URL}/img/red-crown.png`;
const YELLOW_CROWN_SRC = `${process.env.PUBLIC_URL}/img/yellow-crown.png`;

/** P1 = red crown, P2 = yellow crown (Connect 4 disc colors). */
function PlayerScoreCrown({ player }) {
  const isRed = player === P1;
  return (
    <img
      className="game-score-crown-img"
      src={isRed ? RED_CROWN_SRC : YELLOW_CROWN_SRC}
      alt=""
    />
  );
}

export default function Game() {
  const navigate = useNavigate();
  const userData = useMemo(() => readStoredUserData(), []);
  const userId = String(
    userData?.userId ?? userData?.userid ?? userData?.id ?? ""
  ).trim();
  const sessionId = String(userData?.sessionId ?? "").trim();
  const lobbyName = useMemo(() => resolveLobbyName(userData), [userData]);
  const isLocalCpu = isCpuModeSelected();

  const [players, setPlayers] = useState([]);
  const [gameState, setGameState] = useState(null);
  const [lobbyStatus, setLobbyStatus] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [hoverCol, setHoverCol] = useState(-1);
  const [optimisticState, setOptimisticState] = useState(null);
  const [dropAnim, setDropAnim] = useState(null);
  const [clockTick, setClockTick] = useState(0);
  const cpuMoveLock = useRef(false);
  const moveInFlight = useRef(false);
  const timeoutLock = useRef(false);
  const lastAnimatedMoveRef = useRef({ version: -1, row: -1, col: -1 });
  const gameStateRef = useRef(null);
  const roundEndSyncedVersionRef = useRef(-1);
  const navigateToLeaderboardScheduledRef = useRef(false);
  const [redirectSec, setRedirectSec] = useState(3);

  const connect4Ref = useMemo(() => {
    if (!sessionId || !lobbyName) return null;
    return doc(
      database,
      `lobbies/${sessionId}/lobby/${lobbyName}/state/connect4`
    );
  }, [sessionId, lobbyName]);

  const lobbyRef = useMemo(() => {
    if (!sessionId || !lobbyName) return null;
    return doc(database, `lobbies/${sessionId}/lobby/${lobbyName}`);
  }, [sessionId, lobbyName]);

  const leaderboardRef = useMemo(() => {
    if (!sessionId || !lobbyName) return null;
    return getConnect4LeaderboardRef(database, sessionId, lobbyName);
  }, [sessionId, lobbyName]);

  useEffect(() => {
    document.documentElement.classList.add("game-static-bg");
    document.body.classList.add("game-static-bg");
    return () => {
      document.documentElement.classList.remove("game-static-bg");
      document.body.classList.remove("game-static-bg");
    };
  }, []);

  useEffect(() => {
    if (isLocalCpu) {
      if (!userId) {
        setError("Please sign in to play.");
        setLoading(false);
        return undefined;
      }
      const saved = loadCpuGame();
      const initial =
        saved && saved.status === "playing"
          ? parseConnect4State(saved)
          : parseConnect4State(
              startFreshCpuGame(
                userId,
                getPlayerLabel(null, userData, 1)
              )
            );
      setGameState(initial);
      setLobbyStatus("play");
      setLoading(false);
      return undefined;
    }

    if (!sessionId || !lobbyName || !userId) {
      setError("Missing lobby details. Please join from the lobby.");
      setLoading(false);
      return undefined;
    }

    let active = true;
    const usersRef = collection(
      database,
      `lobbies/${sessionId}/lobby/${lobbyName}/users`
    );

    const unsubUsers = onSnapshot(
      query(usersRef, orderBy("timestamp", "asc")),
      (snap) => {
        if (!active) return;
        const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setPlayers(list.filter((p) => p?.status === "live").slice(0, 2));
      }
    );

    const unsubLobby = onSnapshot(lobbyRef, (snap) => {
      if (!active) return;
      const status = String(snap.data()?.status ?? "").toLowerCase();
      setLobbyStatus(status);
      if (status === "waiting") {
        navigate("/waiting", { replace: true });
      }
    });

    const unsubGame = onSnapshot(
      connect4Ref,
      (snap) => {
        if (!active) return;
        if (!snap.exists()) {
          setGameState(null);
          setLoading(false);
          return;
        }
        const parsed = parseConnect4State(snap.data());
        setGameState(parsed);
        setOptimisticState((prev) => {
          if (!prev) return null;
          if (parsed.version >= prev.version) return null;
          return prev;
        });
        setLoading(false);
      },
      (e) => {
        console.error("Connect4 snapshot failed:", e);
        if (active) {
          setError("Could not load game.");
          setLoading(false);
        }
      }
    );

    return () => {
      active = false;
      unsubUsers();
      unsubLobby();
      unsubGame();
    };
  }, [connect4Ref, isLocalCpu, lobbyRef, lobbyName, navigate, sessionId, userId]);

  useEffect(() => {
    gameStateRef.current = gameState;
  }, [gameState]);

  useEffect(() => {
    if (gameState?.status !== "playing") return undefined;
    const id = window.setInterval(() => setClockTick((t) => t + 1), 1000);
    return () => window.clearInterval(id);
  }, [gameState?.status, gameState?.version]);

  const effectiveState = optimisticState || gameState;

  const turnRemainingSec = useMemo(() => {
    if (effectiveState?.status !== "playing") return TURN_LIMIT_SEC;
    const deadline = Number(effectiveState?.turnDeadlineMs);
    if (!deadline) return TURN_LIMIT_SEC;
    return Math.max(0, Math.ceil((deadline - Date.now()) / 1000));
  }, [effectiveState?.status, effectiveState?.turnDeadlineMs, clockTick]);

  const playDropAnim = useCallback((row, col, player, version) => {
    lastAnimatedMoveRef.current = { version, row, col };
    setDropAnim({
      row,
      col,
      player,
      key: `${version}-${row}-${col}`,
    });
  }, []);

  const myPlayer = useMemo(
    () => resolvePlayerForUser(effectiveState || gameState, userId, players),
    [effectiveState, gameState, players, userId]
  );

  const board = useMemo(
    () => boardFromFlat(effectiveState?.cells ?? gameState?.cells),
    [effectiveState?.cells, gameState?.cells]
  );

  const player1 = useMemo(
    () =>
      players.find(
        (p) => String(p?.userId ?? "").trim() === String(gameState?.player1UserId ?? "")
      ) || null,
    [players, gameState?.player1UserId]
  );

  const player2 = useMemo(
    () =>
      players.find(
        (p) => String(p?.userId ?? "").trim() === String(gameState?.player2UserId ?? "")
      ) || null,
    [players, gameState?.player2UserId]
  );

  const meProfile = useMemo(() => {
    if (myPlayer === P1) return { player: player1, slot: 1 };
    if (myPlayer === P2) return { player: player2, slot: 2 };
    return { player: null, slot: 0 };
  }, [myPlayer, player1, player2]);

  const oppProfile = useMemo(() => {
    if (myPlayer === P1) return { player: player2, slot: 2 };
    if (myPlayer === P2) return { player: player1, slot: 1 };
    return { player: players[0] || null, slot: 1 };
  }, [myPlayer, player1, player2, players]);

  const isMyTurn =
    effectiveState?.status === "playing" &&
    effectiveState?.currentPlayer === myPlayer &&
    myPlayer !== EMPTY;

  const isCpuMode = gameState?.mode === GAME_MODE.CPU;
  const isCpuTurn =
    isCpuMode &&
    gameState?.status === "playing" &&
    gameState?.currentPlayer === Number(gameState?.cpuPlayer);

  const headerLabel = useMemo(() => {
    const st = effectiveState || gameState;
    if (!st) return "CONNECT 4";
    if (st.status === "won") {
      if (st.winner === myPlayer) return "YOU WIN!";
      if (st.winner && myPlayer) return "OPPONENT WINS";
      return "GAME OVER";
    }
    if (st.status === "draw") return "DRAW";
    if (isCpuMode && isCpuTurn) return "CPU THINKING...";
    if (isMyTurn) return "YOUR TURN";
    if (myPlayer) return "OPPONENT'S TURN";
    return "CONNECT 4";
  }, [effectiveState, gameState, isCpuMode, isCpuTurn, isMyTurn, myPlayer]);

  const turnUrgent = turnRemainingSec <= 8 && effectiveState?.status === "playing";
  const showTurnTimer =
    effectiveState?.status === "playing" && (!isCpuMode || !isCpuTurn);

  const applyLocalMove = useCallback(
    (col) => {
      const base = optimisticState || gameState;
      if (!base || base.status !== "playing") return;
      if (base.mode === GAME_MODE.CPU && base.currentPlayer === Number(base.cpuPlayer)) {
        return;
      }

      const acting = resolvePlayerForUser(base, userId, players);
      if (!acting || acting !== base.currentPlayer) return;
      if (isColumnFull(boardFromFlat(base.cells), col)) return;

      const patch = applyMove(base, col, acting);
      if (!patch) return;

      const nextState = parseConnect4State({ ...base, ...patch });
      setOptimisticState(null);
      setGameState(nextState);
      saveCpuGame({ ...base, ...patch });
      playDropAnim(
        patch.lastMove.row,
        patch.lastMove.col,
        acting,
        nextState.version
      );
      setError("");
    },
    [gameState, optimisticState, playDropAnim, players, userId]
  );

  const handleColumnClick = useCallback(
    async (col) => {
      if (moveInFlight.current) return;
      if (!userId) return;

      const base = optimisticState || gameState;
      if (!base || base.status !== "playing") return;
      if (isCpuMode && isCpuTurn) return;

      const acting = resolvePlayerForUser(base, userId, players);
      if (!acting || acting !== base.currentPlayer) return;
      if (isColumnFull(board, col)) return;

      if (isLocalCpu) {
        applyLocalMove(col);
        return;
      }

      if (!connect4Ref) return;

      const patch = applyMove(base, col, acting);
      if (!patch) return;

      moveInFlight.current = true;
      setError("");

      const nextState = { ...base, ...patch };
      setOptimisticState(nextState);
      playDropAnim(
        patch.lastMove.row,
        patch.lastMove.col,
        acting,
        nextState.version
      );

      try {
        await runTransaction(database, async (tx) => {
          const snap = await tx.get(connect4Ref);
          if (!snap.exists()) throw new Error("Game not found");
          const current = parseConnect4State(snap.data());
          if (current.status !== "playing") throw new Error("Game ended");

          const player = resolvePlayerForUser(current, userId, players);
          if (!player) throw new Error("Not a player in this match");
          if (player !== current.currentPlayer) throw new Error("Not your turn");

          const serverPatch = applyMove(current, col, player);
          if (!serverPatch) throw new Error("Column full or invalid move");

          tx.set(connect4Ref, { ...current, ...serverPatch });
        });
        setError("");
      } catch (e) {
        console.error("Move failed:", e);
        setOptimisticState(null);
        setDropAnim(null);
        const msg = String(e?.message || "");
        if (msg.includes("Not your turn")) {
          setError("Wait for your turn.");
        } else if (msg.includes("Column full")) {
          setError("That column is full.");
        } else {
          setError("Could not place disc. Try again.");
        }
      } finally {
        moveInFlight.current = false;
      }
    },
    [
      applyLocalMove,
      board,
      connect4Ref,
      gameState,
      isCpuMode,
      isCpuTurn,
      isLocalCpu,
      optimisticState,
      playDropAnim,
      players,
      userId,
    ]
  );

  const runCpuMove = useCallback(async () => {
    if (isLocalCpu) {
      if (cpuMoveLock.current) return;
      cpuMoveLock.current = true;
      try {
        await new Promise((r) => window.setTimeout(r, 650));
        setGameState((prev) => {
          if (!prev || prev.mode !== GAME_MODE.CPU || prev.status !== "playing") {
            return prev;
          }
          if (prev.currentPlayer !== Number(prev.cpuPlayer)) return prev;
          const b = boardFromFlat(prev.cells);
          const col = pickCpuColumn(b, prev.cpuPlayer);
          if (col < 0) return prev;
          const patch = applyMove(prev, col, prev.cpuPlayer);
          if (!patch) return prev;
          const merged = { ...prev, ...patch };
          saveCpuGame(merged);
          return parseConnect4State(merged);
        });
      } finally {
        cpuMoveLock.current = false;
      }
      return;
    }

    if (!connect4Ref || cpuMoveLock.current) return;
    cpuMoveLock.current = true;
    try {
      await new Promise((r) => window.setTimeout(r, 650));
      await runTransaction(database, async (tx) => {
        const snap = await tx.get(connect4Ref);
        if (!snap.exists()) return;
        const current = parseConnect4State(snap.data());
        if (current.mode !== GAME_MODE.CPU || current.status !== "playing") return;
        if (current.currentPlayer !== Number(current.cpuPlayer)) return;

        const b = boardFromFlat(current.cells);
        const col = pickCpuColumn(b, current.cpuPlayer);
        if (col < 0) return;
        const patch = applyMove(current, col, current.cpuPlayer);
        if (!patch) return;
        tx.set(connect4Ref, { ...current, ...patch });
      });
    } catch (e) {
      console.error("CPU move failed:", e);
    } finally {
      cpuMoveLock.current = false;
    }
  }, [connect4Ref, isLocalCpu]);

  useEffect(() => {
    if (isCpuTurn) runCpuMove();
  }, [isCpuTurn, gameState?.version, runCpuMove]);

  const lastMove = effectiveState?.lastMove;
  const winningCells = useMemo(
    () => decodeWinningCells(effectiveState?.winningCells),
    [effectiveState?.winningCells]
  );
  const gameOver =
    effectiveState?.status === "won" || effectiveState?.status === "draw";

  useEffect(() => {
    if (!dropAnim) return;
    const t = window.setTimeout(() => setDropAnim(null), 480);
    return () => window.clearTimeout(t);
  }, [dropAnim?.key]);

  useEffect(() => {
    if (!gameState?.lastMove || optimisticState || moveInFlight.current) return;
    const lm = gameState.lastMove;
    const prev = lastAnimatedMoveRef.current;
    if (
      prev.version === gameState.version &&
      prev.row === lm.row &&
      prev.col === lm.col
    ) {
      return;
    }
    playDropAnim(lm.row, lm.col, lm.player, gameState.version);
  }, [gameState?.lastMove, gameState?.version, optimisticState, playDropAnim]);

  const runTurnTimeout = useCallback(async () => {
    const st = gameStateRef.current;
    if (!st || st.status !== "playing") return;
    const deadline = Number(st.turnDeadlineMs);
    if (!deadline || Date.now() < deadline) return;
    if (
      st.mode === GAME_MODE.CPU &&
      st.currentPlayer === Number(st.cpuPlayer)
    ) {
      return;
    }

    if (isLocalCpu) {
      if (timeoutLock.current) return;
      timeoutLock.current = true;
      try {
        const patch = applyTurnTimeout(st);
        if (!patch) return;
        const next = parseConnect4State({ ...st, ...patch });
        setGameState(next);
        saveCpuGame({ ...st, ...patch });
      } finally {
        timeoutLock.current = false;
      }
      return;
    }

    if (!connect4Ref || timeoutLock.current) return;
    timeoutLock.current = true;
    try {
      await runTransaction(database, async (tx) => {
        const snap = await tx.get(connect4Ref);
        if (!snap.exists()) return;
        const current = parseConnect4State(snap.data());
        const patch = applyTurnTimeout(current);
        if (!patch) return;
        tx.set(connect4Ref, { ...current, ...patch });
      });
    } catch (e) {
      console.error("Turn timeout failed:", e);
    } finally {
      timeoutLock.current = false;
    }
  }, [connect4Ref, isLocalCpu]);

  useEffect(() => {
    if (gameState?.status !== "playing") return undefined;
    const id = window.setInterval(() => {
      runTurnTimeout();
    }, 500);
    return () => window.clearInterval(id);
  }, [gameState?.status, gameState?.version, runTurnTimeout]);

  const canInteract =
    effectiveState?.status === "playing" && isMyTurn && !moveInFlight.current;

  const columnCanDrop = (col) => canInteract && !isColumnFull(board, col);

  useEffect(() => {
    if (!canInteract || gameOver) setHoverCol(-1);
  }, [canInteract, gameOver]);

  useEffect(() => {
    const st = gameState;
    if (!st) return;
    if (st.status !== "won" && st.status !== "draw") return;
    if (roundEndSyncedVersionRef.current === st.version) return;

    if (isLocalCpu) {
      syncCpuSeriesAfterRound(st, getPlayerLabel(player1, userData, 1));
      roundEndSyncedVersionRef.current = st.version;
      return;
    }

    if (!leaderboardRef || !lobbyRef) return;

    let cancelled = false;
    (async () => {
      try {
        await runTransaction(database, async (tx) => {
          const lbSnap = await tx.get(leaderboardRef);
          const existing = lbSnap.exists() ? lbSnap.data() : {};
          const next = buildLeaderboardAfterRound(existing, st, {
            player1Name: getPlayerLabel(player1, null, 1),
            player2Name: getPlayerLabel(player2, null, 2),
          });
          tx.set(leaderboardRef, next);
          tx.set(
            lobbyRef,
            {
              status: "gameover",
              gameType: "connect4",
              winnerSummary: {
                type: "connect4",
                winner: st.winner,
                player1Wins: next.player1Wins,
                player2Wins: next.player2Wins,
              },
            },
            { merge: true }
          );
        });
        if (!cancelled) roundEndSyncedVersionRef.current = st.version;
      } catch (e) {
        console.error("Leaderboard sync failed:", e);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [gameState, isLocalCpu, leaderboardRef, lobbyRef, player1, player2, userData]);

  useEffect(() => {
    if (!gameOver || navigateToLeaderboardScheduledRef.current) return;
    navigateToLeaderboardScheduledRef.current = true;
    setRedirectSec(3);

    const tick = window.setInterval(() => {
      setRedirectSec((s) => (s > 0 ? s - 1 : 0));
    }, 1000);

    const go = window.setTimeout(() => {
      navigate("/leaderboard", { replace: true });
    }, 3000);

    return () => {
      window.clearInterval(tick);
      window.clearTimeout(go);
    };
  }, [gameOver, navigate]);

  const resultPopup = useMemo(() => {
    const st = effectiveState || gameState;
    if (!st || (st.status !== "won" && st.status !== "draw")) return null;

    if (st.status === "draw") {
      return {
        type: "draw",
        title: "DRAW",
        message: "The board is full — no winner this round.",
      };
    }

    const winnerSlot = st.winner === P1 ? 1 : 2;
    const winnerUser =
      st.winner === P1 ? player1 : st.winner === P2 ? player2 : null;
    const winnerName = isCpuMode && st.winner === P2
      ? "Computer"
      : getPlayerLabel(winnerUser, null, winnerSlot);
    const iWon = st.winner === myPlayer && myPlayer !== EMPTY;

    return {
      type: "win",
      title: iWon ? "YOU WIN!" : "GAME OVER",
      message: iWon
        ? "You connected four in a row!"
        : `${winnerName} connected four in a row!`,
      winnerName,
      winnerCharacter: normalizeCharacterName(winnerUser?.character) ||
        (st.winner === P1 ? "c1" : "c2"),
      winnerDisc: st.winner,
      iWon,
    };
  }, [
    effectiveState,
    gameState,
    isCpuMode,
    myPlayer,
    player1,
    player2,
  ]);

  const myWins =
    myPlayer === P1
      ? Number(gameState?.player1Wins || 0)
      : myPlayer === P2
        ? Number(gameState?.player2Wins || 0)
        : 0;
  const oppWins =
    myPlayer === P1
      ? Number(gameState?.player2Wins || 0)
      : myPlayer === P2
        ? Number(gameState?.player1Wins || 0)
        : 0;

  if (!isLocalCpu && (!sessionId || !lobbyName)) {
    return (
      <div className="game-page">
        <p className="game-status-msg">Join a lobby to play.</p>
        <button type="button" className="game-rematch-btn" onClick={() => navigate("/modes")}>
          Choose mode
        </button>
      </div>
    );
  }

  return (
    <div className="game-page">
      <div className="game-decor" aria-hidden="true">
        <span className="game-decor-ring game-decor-ring--1" />
        <span className="game-decor-ring game-decor-ring--2" />
        <span className="game-decor-ring game-decor-ring--3" />
        <span className="game-decor-chip game-decor-chip--red" />
        <span className="game-decor-chip game-decor-chip--yellow" />
      </div>

      <header className="game-header">
        <h1
          className={`game-turn-label ${isMyTurn ? "game-turn-label--active" : ""} ${
            gameOver ? "game-turn-label--over" : ""
          }`}
        >
          {headerLabel}
        </h1>
        {showTurnTimer ? (
          <p
            className={[
              "game-header-timer",
              isMyTurn ? "game-header-timer--yours" : "",
              turnUrgent ? "game-header-timer--urgent" : "",
            ]
              .filter(Boolean)
              .join(" ")}
            aria-live="polite"
          >
            {formatTurnClock(turnRemainingSec)}
          </p>
        ) : null}
      </header>

      {loading ? <p className="game-status-msg">Loading game...</p> : null}
      {error ? (
        <p className="game-status-msg game-status-msg--error" role="alert">
          {error}
        </p>
      ) : null}

      {!loading && !gameState && lobbyStatus !== "play" ? (
        <p className="game-status-msg">Waiting for host to start the match...</p>
      ) : null}

      <div className="game-layout">
        <aside className="game-sidebar game-sidebar--left">
          <article
            className={`game-panel game-player-card ${
              isMyTurn ? "game-player-card--active-turn" : ""
            }`}
          >
            <div className="game-player-avatar-wrap">
              <img
                className="game-player-avatar"
                src={characterAvatarSrc(
                  meProfile.player?.character || userData?.character
                )}
                alt=""
              />
            </div>
            <span className="game-player-badge game-player-badge--you">YOU</span>
            <p className="game-player-name">
              {getPlayerLabel(
                meProfile.player,
                userData,
                meProfile.slot || 1
              )}
            </p>
            <div className="game-player-score">
              <PlayerScoreCrown player={myPlayer} />
              <span className="game-score-value">{myWins}</span>
            </div>
          </article>

          <article className="game-panel game-info-card">
            <h2 className="game-info-title">GAME INFO</h2>
            <div className="game-info-sections">
              <section className="game-info-section">
                <span className="game-info-section-label">MODE</span>
                <div className="game-info-section-row">
                  <span className="game-info-icon" aria-hidden="true">
                    <GameInfoModeIcon cpu={isCpuMode} />
                  </span>
                  <span className="game-info-value">
                    {isCpuMode ? "vs CPU" : "1v1 Match"}
                  </span>
                </div>
              </section>
              <div className="game-info-divider" aria-hidden="true" />
              <section className="game-info-section">
                <span className="game-info-section-label">MOVES</span>
                <div className="game-info-section-row">
                  <span className="game-info-icon" aria-hidden="true">
                    <GameInfoMovesIcon />
                  </span>
                  <span className="game-info-value">
                    {Number(gameState?.moveCount || 0)}
                  </span>
                </div>
              </section>
            </div>
          </article>
        </aside>

        <main className="game-center" aria-label="Connect Four board">
          <div className="connect4-wrap">
            <div
              className={`connect4-frame ${gameOver ? "connect4-frame--locked" : ""}`}
            >
              <div className="connect4-columns" role="group" aria-label="Choose column">
                {Array.from({ length: COLS }, (_, col) => {
                  const previewRow =
                    hoverCol === col && canInteract
                      ? getDropRow(board, col)
                      : -1;
                  const colActive = canInteract && hoverCol === col;
                  const colPlayable = columnCanDrop(col);

                  return (
                    <div
                      key={col}
                      className={[
                        "connect4-column",
                        colActive ? "connect4-column--hover" : "",
                        colPlayable ? "connect4-column--playable" : "",
                        colActive && myPlayer === P1
                          ? "connect4-column--preview-p1"
                          : "",
                        colActive && myPlayer === P2
                          ? "connect4-column--preview-p2"
                          : "",
                      ]
                        .filter(Boolean)
                        .join(" ")}
                      onMouseEnter={() => {
                        if (canInteract) setHoverCol(col);
                      }}
                      onMouseLeave={() => setHoverCol(-1)}
                      onClick={() => {
                        if (columnCanDrop(col)) handleColumnClick(col);
                      }}
                      onKeyDown={(e) => {
                        if (
                          (e.key === "Enter" || e.key === " ") &&
                          columnCanDrop(col)
                        ) {
                          e.preventDefault();
                          handleColumnClick(col);
                        }
                      }}
                      role="button"
                      tabIndex={colPlayable ? 0 : -1}
                      aria-label={`Column ${col + 1}${
                        isColumnFull(board, col) ? ", full" : ""
                      }`}
                      aria-disabled={!colPlayable}
                    >
                      <span className="connect4-col-arrow" aria-hidden="true">
                        ▼
                      </span>
                      <span className="connect4-column-beam" aria-hidden="true" />

                      <div className="connect4-column-slots">
                        {Array.from({ length: ROWS }, (_, row) => {
                          const cell = board[row][col];
                          const isWin = isWinningCell(winningCells, row, col);
                          const isGhost =
                            previewRow === row && cell === EMPTY && colActive;
                          const isDropping =
                            dropAnim?.row === row && dropAnim?.col === col;
                          const showDisc = cell === P1 || cell === P2;

                          return (
                            <div
                              key={row}
                              className={[
                                "connect4-slot",
                                isWin ? "connect4-slot--win" : "",
                              ]
                                .filter(Boolean)
                                .join(" ")}
                            >
                              {isGhost ? (
                                <span
                                  className={`connect4-disc connect4-disc--ghost connect4-disc--p${myPlayer}`}
                                  aria-hidden="true"
                                />
                              ) : null}
                              {showDisc ? (
                                <span
                                  className={[
                                    "connect4-disc",
                                    cell === P1
                                      ? "connect4-disc--p1"
                                      : "connect4-disc--p2",
                                    isDropping ? "connect4-disc--drop" : "",
                                    isWin ? "connect4-disc--win" : "",
                                  ]
                                    .filter(Boolean)
                                    .join(" ")}
                                >
                                  <span className="connect4-disc-crown">♔</span>
                                </span>
                              ) : null}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

        </main>

        <aside className="game-sidebar game-sidebar--right">
          <article
            className={`game-panel game-player-card game-player-card--opponent ${
              effectiveState?.status === "playing" && myPlayer && !isMyTurn
                ? "game-player-card--active-turn"
                : ""
            }`}
          >
            <div className="game-player-avatar-wrap">
              <img
                className={`game-player-avatar ${
                  isCpuMode ? "game-player-avatar--cpu" : ""
                }`}
                src={
                  isCpuMode
                    ? `${process.env.PUBLIC_URL}/img/computer.png`
                    : characterAvatarSrc(
                        oppProfile.player?.character ||
                          (oppProfile.slot === 2 ? "c2" : "c1")
                      )
                }
                alt=""
              />
            </div>
            <span className="game-player-badge game-player-badge--opponent">
              {isCpuMode ? "CPU" : "OPPONENT"}
            </span>
            <p className="game-player-name">
              {isCpuMode
                ? "Computer"
                : getPlayerLabel(oppProfile.player, null, oppProfile.slot || 2)}
            </p>
            <div className="game-player-score game-player-score--opponent">
              <PlayerScoreCrown
                player={myPlayer === P1 ? P2 : myPlayer === P2 ? P1 : P2}
              />
              <span className="game-score-value">{oppWins}</span>
            </div>
          </article>
        </aside>
      </div>

      {resultPopup ? (
        <div className="game-result-overlay" role="presentation">
          <div
            className={`game-result-card ${
              resultPopup.type === "draw"
                ? "game-result-card--draw"
                : resultPopup.iWon
                  ? "game-result-card--win"
                  : "game-result-card--lose"
            }`}
            role="dialog"
            aria-modal="true"
            aria-labelledby="game-result-title"
          >
            <div className="game-result-avatar-wrap">
              {resultPopup.type === "win" ? (
                <img
                  src={characterAvatarSrc(resultPopup.winnerCharacter)}
                  alt=""
                  className="game-result-avatar"
                />
              ) : (
                <span className="game-result-draw-icon" aria-hidden="true">
                  =
                </span>
              )}
              {resultPopup.type === "win" ? (
                <span
                  className={`game-result-disc game-result-disc--p${resultPopup.winnerDisc}`}
                  aria-hidden="true"
                />
              ) : null}
            </div>
            <h2 id="game-result-title" className="game-result-title">
              {resultPopup.title}
            </h2>
            <p className="game-result-message">{resultPopup.message}</p>
            {resultPopup.type === "win" && !resultPopup.iWon ? (
              <p className="game-result-winner-name">{resultPopup.winnerName}</p>
            ) : null}
            <p className="game-result-redirect">
              Going to leaderboard in {redirectSec}s…
            </p>
          </div>
        </div>
      ) : null}
    </div>
  );
}
